/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react'
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import MeasureType from 'enums/MeasureType'
import { Trans } from 'react-i18next'
import { ChartConfig, ExplanationData } from '../types'
import { ExplanationTooltip } from './tooltip'

interface RawExplanationData {
  tooltipContent: string
  link: string
}

// Overload signatures
export function getExplanation(
  chartConfig: ChartConfig,
  viewOption: InsightViewOptionStrings
): ExplanationData
export function getExplanation(key: string, viewOption: InsightViewOptionStrings): ExplanationData
// Implementation
export function getExplanation(
  chartConfigOrKey: ChartConfig | string,
  viewOption: InsightViewOptionStrings
) {
  const map = getExplanationMap(viewOption)
  if (!map) return { tooltipContent: '', link: '' }

  const key = typeof chartConfigOrKey === 'string' ? chartConfigOrKey : chartConfigOrKey.category

  const explanation = map[key]
  if (!explanation) return { tooltipContent: '', link: '' }

  const { tooltipContent: translationKey, link } = explanation
  return {
    tooltipContent: <ExplanationTooltip content={<Trans i18nKey={translationKey} />} />,
    link
  }
}

const getExplanationMap = (viewOption: InsightViewOptionStrings) => {
  switch (viewOption) {
    case InsightViewOption.OVERVIEW:
      return OverviewExplanation
    case InsightViewOption.OWNED_PARTNERED_EARNED:
      return ConvergedMediaExplanation
    case InsightViewOption.CHANNELS:
      return DomainExplanation
    default:
      return undefined
  }
}

export const OverviewExplanation: Record<string, RawExplanationData> = {
  [MeasureType.MENTIONS]: {
    tooltipContent: 'summaryMetricsOverviewExplanation.mentions',
    link: '8258221'
  },
  [MeasureType.POSTS]: {
    tooltipContent: 'summaryMetricsOverviewExplanation.posts',
    link: '8258221'
  },
  [MeasureType.NET_SENTIMENT]: {
    tooltipContent: 'summaryMetricsOverviewExplanation.netSentiment',
    link: '8241448'
  },
  [MeasureType.PASSION_INTENSITY]: {
    tooltipContent: 'summaryMetricsOverviewExplanation.passionIntensity',
    link: '8241448'
  },
  [MeasureType.ENGAGEMENTS]: {
    tooltipContent: 'summaryMetricsOverviewExplanation.engagements',
    link: '8241485'
  },
  [MeasureType.ENGAGEMENT_RATIO]: {
    tooltipContent: 'summaryMetricsOverviewExplanation.engagementRatio',
    link: '8241485'
  },
  [MeasureType.IMPRESSIONS]: {
    tooltipContent: 'summaryMetricsOverviewExplanation.impressions',
    link: '8241511'
  },
  [MeasureType.UNIQUE_AUTHORS]: {
    tooltipContent: 'summaryMetricsOverviewExplanation.uniqueAuthors',
    link: '8239858'
  }
}

export const ConvergedMediaExplanation: Record<string, RawExplanationData> = {
  [MeasureType.MENTIONS]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.mentions',
    link: '8258221'
  },
  [MeasureType.POSTS]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.posts',
    link: '8258221'
  },
  [MeasureType.NET_SENTIMENT]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.netSentiment',
    link: '8241448'
  },
  [MeasureType.POSITIVES]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.positives',
    link: '8258226'
  },
  [MeasureType.NEGATIVES]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.negatives',
    link: '8258226'
  },
  [MeasureType.PASSION_INTENSITY]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.passionIntensity',
    link: '8241448'
  },
  [MeasureType.ENGAGEMENTS]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.engagements',
    link: '8241485'
  },
  [MeasureType.COMMENTS]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.comments',
    link: '8241485'
  },
  [MeasureType.SHARES]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.shares',
    link: '8241485'
  },
  [MeasureType.LIKES]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.likes',
    link: '8241485'
  },
  [MeasureType.SCORE]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.score',
    link: '8241485'
  },
  [MeasureType.ENGAGEMENT_RATIO]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.engagementRatio',
    link: '8241485'
  },
  [MeasureType.IMPRESSIONS]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.impressions',
    link: '8241511'
  },
  [MeasureType.UNIQUE_AUTHORS]: {
    tooltipContent: 'summaryMetricsConvergedMediaExplanation.uniqueAuthors',
    link: '8239858'
  }
}

export const DomainExplanation: Record<string, RawExplanationData> = {
  [MeasureType.MENTIONS]: {
    tooltipContent: 'summaryMetricsDomainExplanation.mentions',
    link: '8258221'
  },
  [MeasureType.POSTS]: {
    tooltipContent: 'summaryMetricsDomainExplanation.posts',
    link: '8258221'
  },
  [MeasureType.NET_SENTIMENT]: {
    tooltipContent: 'summaryMetricsDomainExplanation.netSentiment',
    link: '8241448'
  },
  [MeasureType.POSITIVES]: {
    tooltipContent: 'summaryMetricsDomainExplanation.positives',
    link: '8258226'
  },
  [MeasureType.NEGATIVES]: {
    tooltipContent: 'summaryMetricsDomainExplanation.negatives',
    link: '8258226'
  },
  [MeasureType.PASSION_INTENSITY]: {
    tooltipContent: 'summaryMetricsDomainExplanation.passionIntensity',
    link: '8241448'
  },
  [MeasureType.ENGAGEMENTS]: {
    tooltipContent: 'summaryMetricsDomainExplanation.engagements',
    link: '8241485'
  },
  [MeasureType.COMMENTS]: {
    tooltipContent: 'summaryMetricsDomainExplanation.comments',
    link: '8241485'
  },
  [MeasureType.SHARES]: {
    tooltipContent: 'summaryMetricsDomainExplanation.shares',
    link: '8241485'
  },
  [MeasureType.LIKES]: {
    tooltipContent: 'summaryMetricsDomainExplanation.likes',
    link: '8241485'
  },
  [MeasureType.VIEWS]: {
    tooltipContent: 'summaryMetricsDomainExplanation.views',
    link: '8241485'
  },
  [MeasureType.SCORE]: {
    tooltipContent: 'summaryMetricsDomainExplanation.score',
    link: '8241485'
  },
  [MeasureType.ENGAGEMENT_RATIO]: {
    tooltipContent: 'summaryMetricsDomainExplanation.engagementRatio',
    link: '8241485'
  },
  [MeasureType.IMPRESSIONS]: {
    tooltipContent: 'summaryMetricsDomainExplanation.impressions',
    link: '8241511'
  },
  [MeasureType.UNIQUE_AUTHORS]: {
    tooltipContent: 'summaryMetricsDomainExplanation.uniqueAuthors',
    link: '8239858'
  }
}
